import React, { useState, useEffect } from 'react';
import api from '../services/api';
import '../styles/contact.css';

const Contact = () => {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState({
    submitting: false,
    success: false,
    error: null
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadCompanyInfo();
  }, []);

  const loadCompanyInfo = async () => {
    try {
      const response = await api.get('/company.php');
      setCompany(response.data.company);
    } catch (err) {
      console.error('Failed to load company info:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setFormStatus({ submitting: true, success: false, error: null });
    
    try {
      const response = await api.post('/contact.php', formData);
      
      if (response.data.success) {
        setFormStatus({ submitting: false, success: true, error: null });
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
      } else {
        setFormStatus({ 
          submitting: false, 
          success: false, 
          error: response.data.error || 'Failed to send message' 
        });
      }
    } catch (err) {
      setFormStatus({ 
        submitting: false, 
        success: false, 
        error: err.response?.data?.error || 'Network error. Please try again.' 
      });
    }
  };

  if (loading) {
    return (
      <div className="contact-loading">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="container">
          <h1>Get in Touch</h1>
          <p>We'd love to hear from you. Send us a message and we'll respond as soon as possible.</p>
        </div>
      </section>

      <div className="container">
        <div className="contact-grid">
          {/* Contact Form */}
          <div className="contact-form-section">
            <h2>Send us a Message</h2>
            
            {formStatus.success ? (
              <div className="success-message">
                <div className="success-icon">✓</div>
                <h3>Thank You!</h3>
                <p>Your message has been sent successfully. We'll get back to you soon.</p>
                <button 
                  onClick={() => setFormStatus({ submitting: false, success: false, error: null })}
                  className="btn-primary"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="contact-form">
                {formStatus.error && (
                  <div className="error-alert">{formStatus.error}</div>
                )}
                
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={errors.name ? 'error' : ''}
                    placeholder="Enter your full name"
                  />
                  {errors.name && <span className="error-message">{errors.name}</span>}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={errors.email ? 'error' : ''}
                      placeholder="your@email.com"
                    />
                    {errors.email && <span className="error-message">{errors.email}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">Phone</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+254 700 000000"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="subject">Subject *</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className={errors.subject ? 'error' : ''}
                    placeholder="What is this about?"
                  />
                  {errors.subject && <span className="error-message">{errors.subject}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className={errors.message ? 'error' : ''}
                    rows="6"
                    placeholder="Tell us more about your inquiry..."
                  />
                  {errors.message && <span className="error-message">{errors.message}</span>}
                </div>

                <button 
                  type="submit" 
                  className="btn-primary btn-large"
                  disabled={formStatus.submitting}
                >
                  {formStatus.submitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            )}
          </div>

          {/* Contact Information */}
          <div className="contact-info-section">
            <h2>Contact Information</h2>
            
            <div className="info-cards">
              <div className="info-card">
                <div className="info-icon">📍</div>
                <div>
                  <h3>Visit Us</h3>
                  <p>{company?.address || 'Nairobi, Kenya'}</p>
                </div>
              </div>

              <div className="info-card">
                <div className="info-icon">📞</div>
                <div>
                  <h3>Call Us</h3>
                  <p>{company?.phone || '+254 726 642 205'}</p>
                  <p className="small">Mon-Fri, 8am-6pm</p>
                </div>
              </div>

              <div className="info-card">
                <div className="info-icon">✉️</div>
                <div>
                  <h3>Email Us</h3>
                  <p>{company?.email || 'info@harvyq.com'}</p>
                  <p className="small">We reply within 24 hours</p>
                </div>
              </div>

              <div className="info-card">
                <div className="info-icon">⏰</div>
                <div>
                  <h3>Business Hours</h3>
                  <p>Monday - Friday: 8:00 - 18:00</p>
                  <p>Saturday: 9:00 - 13:00</p>
                  <p>Sunday: Closed</p>
                </div>
              </div>
            </div>

            <div className="social-links">
              <h3>Follow Us</h3>
              <div className="social-icons">
                {company?.facebook && (
                  <a href={company.facebook} target="_blank" rel="noopener noreferrer" className="social-icon">
                    📘
                  </a>
                )}
                {company?.twitter && (
                  <a href={company.twitter} target="_blank" rel="noopener noreferrer" className="social-icon">
                    🐦
                  </a>
                )}
                {company?.instagram && (
                  <a href={company.instagram} target="_blank" rel="noopener noreferrer" className="social-icon">
                    📷
                  </a>
                )}
                {company?.linkedin && (
                  <a href={company.linkedin} target="_blank" rel="noopener noreferrer" className="social-icon">
                    🔗
                  </a>
                )}
              </div>
            </div>

            {/* Map */}
            <div className="map-container">
              <iframe
                title="Office Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d15955.39686987857!2d36.81763495!3d-1.286389!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x182f10d4b2b3b3b3%3A0x3b3b3b3b3b3b3b3b!2sNairobi%2C%20Kenya!5e0!3m2!1sen!2s!4v1620000000000!5m2!1sen!2s"
                width="100%"
                height="300"
                style={{ border: 0, borderRadius: '8px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          
          <div className="faq-grid">
            <div className="faq-item">
              <h3>How long is the free trial?</h3>
              <p>We offer a 14-day free trial with full access to all features. No credit card required.</p>
            </div>
            
            <div className="faq-item">
              <h3>Can I change my plan later?</h3>
              <p>Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately.</p>
            </div>
            
            <div className="faq-item">
              <h3>Do you offer support?</h3>
              <p>Yes, we offer email support for all plans and priority support for Professional and Enterprise plans.</p>
            </div>
            
            <div className="faq-item">
              <h3>Is my data secure?</h3>
              <p>Absolutely. We use industry-standard encryption and security practices to protect your data.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;